import React, { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    title?: ReactNode;
    children: ReactNode;
    width?: string;
}

export default function Modal({ isOpen, onClose, title, children, width = "max-w-lg" }: Props) {
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
                type="button"
                aria-label="close"
                onClick={onClose}
                className="absolute inset-0 cursor-default appearance-none border-0 bg-black/30"
            />
            <div
                className={[
                    "relative flex max-h-[90vh] w-full flex-col rounded-xl border border-border bg-surface shadow-xl",
                    width,
                ].join(" ")}
            >
                {title !== undefined && (
                    <header className="flex items-center justify-between border-b border-border px-5 py-4">
                        <div className="min-w-0 truncate pr-2 text-sm font-medium text-ink">
                            {title}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="close"
                            className="flex h-8 w-8 shrink-0 appearance-none items-center justify-center rounded-full! border-0 bg-transparent p-0 text-ink-muted hover:bg-surface-3 hover:text-ink"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </header>
                )}
                <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
            </div>
        </div>,
        document.body,
    );
}
